import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { flushSync } from "react-dom";
import { createTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { ptBR } from "@material-ui/core/locale";
import { CssBaseline } from "@material-ui/core";

const ThemeContext = createContext();

const CONSIG_GREEN = "#1E9855";
const CONSIG_GREEN_LIGHT = "#2ecc71";
const CONSIG_GREEN_DARK = "#17803f";

const buildTheme = (darkMode) =>
  createTheme({

    

    
    typography: {
      fontFamily: "'Poppins', 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      fontWeightLight: 400,
      fontWeightRegular: 500,
      fontWeightMedium: 600,
      fontWeightBold: 700,
      h1: { fontWeight: 700, letterSpacing: "-0.02em" },
      h2: { fontWeight: 700, letterSpacing: "-0.02em" },
      h3: { fontWeight: 700, letterSpacing: "-0.015em" },
      h4: { fontWeight: 700, letterSpacing: "-0.015em" },
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      h6: { fontWeight: 700, letterSpacing: "-0.01em" },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      body1: { fontWeight: 500 },
      body2: { fontWeight: 500 },
      button: { fontWeight: 600, textTransform: "none" },
      caption: { fontWeight: 500 },
      overline: { fontWeight: 600, letterSpacing: "0.06em" },
    },
    scrollbarStyles: {
      "&::-webkit-scrollbar": {
        width: "9px",
        height: "9px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: darkMode ? "rgba(74,222,128,0.35)" : "rgba(30,152,85,0.45)",
        borderRadius: "8px",
        border: "2px solid transparent",
        backgroundClip: "content-box",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
      },
    },
    palette: {
      type: darkMode ? "dark" : "light",
      primary: {
        main: CONSIG_GREEN,
        light: CONSIG_GREEN_LIGHT,
        dark: CONSIG_GREEN_DARK,
        contrastText: "#ffffff",
      },
      secondary: {
        main: CONSIG_GREEN_LIGHT,
        contrastText: "#ffffff",
      },
      // Modo escuro = tema espacial: preto com toques de verde
      background: darkMode
        ? { default: "#0b0f0e", paper: "#141a18" }
        : { default: "#f3f5f4", paper: "#ffffff" },
      text: darkMode
        ? { primary: "#e7ece9", secondary: "#9aa8a2" }
        : { primary: "#111a16", secondary: "#5f6b66" },
      ...(darkMode && { divider: "rgba(255,255,255,0.08)" }),
    },
    shape: {
      borderRadius: 8,
    },
    overrides: {
      
      ...(darkMode && {
        MuiTypography: {
          colorPrimary: {
            color: "#ffffff",
          },
        },
      }),
      MuiCssBaseline: {
        "@global": {

          
          "html, body, #root": {
            fontFamily:
              "'Poppins', 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
            fontWeight: 500,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "input, textarea, select, button, table, th, td, label": {
            fontFamily: "inherit",
          },
          "h1, h2, h3, h4, h5, h6, th, strong, b": {
            fontWeight: 700,
          },
          // barra de rolagem verde e visível nos dois modos
          "*::-webkit-scrollbar": { width: "9px", height: "9px" },
          "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: darkMode
              ? "rgba(74,222,128,0.35)"
              : "rgba(30,152,85,0.45)",
            borderRadius: "8px",
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: darkMode
              ? "rgba(74,222,128,0.6)"
              : "rgba(30,152,85,0.75)",
          },
          
          "input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active": {
            WebkitBoxShadow: `0 0 0 1000px ${darkMode ? "#141a18" : "#ffffff"} inset`,
            WebkitTextFillColor: darkMode ? "#e2e8f0" : "#101828",
            caretColor: darkMode ? "#e2e8f0" : "#101828",
            transition: "background-color 9999s ease-in-out 0s",
          },
        },
      },
      // ── Visual novo do sistema inteiro (só aparência) ──
      MuiButton: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 10,
          transition: "background-color .2s ease, box-shadow .2s ease, transform .15s ease",
          "&:active": { transform: "scale(0.98)" },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
          boxShadow: "0 4px 12px rgba(34,197,94,0.25)",
          "&:hover": {
            background: "linear-gradient(135deg, #15803d 0%, #16a34a 100%)",
            boxShadow: "0 6px 16px rgba(34,197,94,0.35)",
          },
          "&.Mui-disabled": {
            background: darkMode ? "rgba(255,255,255,0.08)" : "#e4e7ec",
            boxShadow: "none",
          },
        },
        outlinedPrimary: {
          borderColor: "rgba(30,152,85,0.5)",
          "&:hover": { backgroundColor: "rgba(30,152,85,0.08)" },
        },
      },
      MuiIconButton: {
        root: {
          transition: "background-color .2s ease, color .2s ease, transform .15s ease",
          "&:active": { transform: "scale(0.94)" },
        },
      },
      MuiPaper: {
        rounded: { borderRadius: 12 },
        elevation1: {
          boxShadow: darkMode
            ? "0 1px 3px rgba(0,0,0,0.45)"
            : "0 1px 2px rgba(17,24,20,0.04), 0 4px 16px rgba(17,24,20,0.05)",
        },
        outlined: {
          border: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e6ebe8",
        },
      },
      MuiDialog: {
        paper: {
          borderRadius: 18,
          ...(darkMode && {
            backgroundColor: "#141a18",
            border: "1px solid rgba(255,255,255,0.08)",
          }),
        },
      },
      MuiDialogTitle: {
        root: {
          borderBottom: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #eef1ef",
          "& h2": { fontWeight: 700 },
        },
      },
      MuiDialogActions: {
        root: {
          padding: "12px 20px",
          borderTop: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #eef1ef",
        },
      },
      MuiMenu: {
        paper: {
          borderRadius: 12,
          border: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e6ebe8",
          boxShadow: darkMode
            ? "0 12px 32px rgba(0,0,0,0.5)"
            : "0 12px 32px rgba(17,24,20,0.10)",
        },
      },
      MuiMenuItem: {
        root: {
          borderRadius: 8,
          margin: "1px 6px",
          fontSize: "0.9rem",
          "&:hover": { backgroundColor: "rgba(30,152,85,0.08)" },
          "&.Mui-selected, &.Mui-selected:hover": {
            backgroundColor: "rgba(30,152,85,0.14)",
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: 10,
          transition: "box-shadow .2s ease",
          "&:hover $notchedOutline": {
            borderColor: "rgba(30,152,85,0.6)",
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 3px rgba(30,152,85,0.14)",
          },
        },
        notchedOutline: {
          borderColor: darkMode ? "rgba(255,255,255,0.12)" : "#dde3e0",
        },
      },
      MuiTooltip: {
        tooltip: {
          backgroundColor: darkMode ? "#1f2724" : "#111614",
          border: "1px solid rgba(74,222,128,0.25)",
          fontSize: "0.75rem",
          fontWeight: 500,
          borderRadius: 8,
          padding: "6px 10px",
        },
        arrow: { color: darkMode ? "#1f2724" : "#111614" },
      },
      MuiChip: {
        root: { fontWeight: 600, borderRadius: 8 },
      },
      MuiSwitch: {
        switchBase: {
          "&$checked": { color: "#22c55e" },
          "&$checked + $track": { backgroundColor: "#22c55e", opacity: 0.5 },
        },
      },
      MuiTabs: {
        indicator: { borderRadius: 3, height: 3 },
      },
      MuiTab: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          "&.Mui-selected": { color: "#1E9855" },
        },
      },
      MuiTableRow: {
        root: {
          transition: "background-color .15s ease",
          "&$hover:hover": {
            backgroundColor: darkMode ? "rgba(255,255,255,0.04)" : "#f5f8f6",
          },
        },
      },
      MuiTableHead: {
        root: {
          backgroundColor: darkMode ? "#111614" : "#f6f8f7",
        },
      },
      MuiTableCell: {
        head: {
          fontWeight: 700,
          color: darkMode ? "#6ee7a0" : "#1E9855",
          fontSize: "0.72rem",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          borderBottom: darkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e3e8e5",
        },
        body: {
          color: darkMode ? "#d6ddd9" : "#1f2a25",
          borderBottom: darkMode ? "1px solid rgba(255,255,255,0.06)" : "1px solid #eef1ef",
        },
      },
    },
  }, ptBR);

export const ThemeProvider = ({ children }) => {
  // O tema espacial (escuro) virou o padrão. A chave é nova de propósito:
  // quem tinha "claro" salvo na chave antiga também passa a ver o tema novo,
  // e continua podendo voltar para o claro pelo menu do usuário.
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("temaEspacial") !== "false"
  );

  const toggleTheme = () => {
    const trocar = () => {
      // flushSync: a tela nova precisa estar pronta dentro do
      // startViewTransition para o navegador fazer o fade entre as duas
      flushSync(() => {
        setDarkMode((prevMode) => {
          const next = !prevMode;
          localStorage.setItem("temaEspacial", String(next));
          return next;
        });
      });
    };

    // Sem transições CSS durante a troca: antes cada elemento mudava de cor
    // num ritmo diferente e a tela ficava "quebrada" por um instante.
    const html = document.documentElement;
    html.classList.add("trocando-tema");
    const liberar = () => setTimeout(() => html.classList.remove("trocando-tema"), 50);

    if (document.startViewTransition) {
      const vt = document.startViewTransition(trocar);
      vt.finished.finally(liberar);
    } else {
      trocar();
      liberar();
    }
  };

  const theme = useMemo(() => buildTheme(darkMode), [darkMode]);

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
